"use client"
import IconRoundChevronLeft from "@/app/(site)/_components/Atoms/Icons/IconRoundChevronLeft"
import IconRoundChevronRight from "@/app/(site)/_components/Atoms/Icons/IconRoundChevronRight"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { useProductCardContext } from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductCardContext"
import { useIsMobile } from "@/app/(site)/_utils/_hooks/useIsMobile"
import { type RelatedVariant } from "@/lib/content-service/types/ContentServiceProduct"
import {
    bunnyLoader,
    checkIfImageSrcIsCentraImage,
    transformToBunnyCdn
} from "@/lib/sanityLoader"
import { FOCUS_VISIBLE_INSET_CLASS } from "@/src/utils/a11y/focusVisible"
import { HIT_TARGET_CLASS } from "@/src/utils/a11y/hitTarget"
import { clsx } from "@/src/utils/clsx"
import { getProductVariantLinkLabel } from "@/src/utils/product/getProductVariantLinkLabel"
import { t } from "@/translations"
import Image from "next/image"
import { Fragment, useEffect, useRef, useState } from "react"

// Hardcoded square size requested for swatch thumbnails. The slot renders at
// ~28-40px; 64px keeps it crisp up to ~2x without shipping the oversized
// images the DPR srcset was picking. Bump for sharper high-DPI if needed.
const THUMBNAIL_SIZE_PX = 32

type ProductCardThumbnailsProps = {
    allVariants: RelatedVariant[]
    langCode: string
    showBenefits?: boolean
    handleGoToSwatchTracking: (swatch: RelatedVariant, swatchIndex: number) => void
    isAboveTheFold: boolean
}

function Thumbnail({
    swatch,
    index,
    handleGoToSwatchTracking,
    isAboveTheFold,
    langCode
}: {
    swatch: RelatedVariant
    index: number
    handleGoToSwatchTracking: (swatch: RelatedVariant, swatchIndex: number) => void
    isAboveTheFold: boolean
    langCode: string
}) {
    const { setThumbnailSelected, thumbnailSelected, handleChangeVariant } =
        useProductCardContext()
    const swatchImage =
        Array.isArray(swatch.media) &&
        swatch.media.find(i => i.attributes?.image_type_usage_type === "swatch" && i)?.url

    const isMobile = useIsMobile()

    const thumbnailStyling = `relative h-28 w-28 cursor-pointer p-2 sm:h-32 sm:w-32 lg:h-40 lg:w-40 lg:p-4 ${index === 0 ? "group/first" : ""}`

    const handleActiveThumbnailClick = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
        e.preventDefault()
        setThumbnailSelected(index)
        handleChangeVariant(swatch)
        handleGoToSwatchTracking(swatch, index)
    }

    const handleMouseEnter = () => {
        // Don't trigger variant change on mobile - only on desktop hover
        if (isMobile) return
        handleChangeVariant(swatch)
    }

    const handleMouseLeave = () => {
        if (thumbnailSelected === 0 && !isMobile) {
            setThumbnailSelected(undefined)
        }
    }

    const selectedThumbnail =
        thumbnailSelected === index || (index === 0 && thumbnailSelected === undefined)

    const borderStyling = selectedThumbnail
        ? "after:absolute after:-bottom-2 lg:after:-bottom-4 after:left-0 after:z-10 after:h-1 after:w-full after:bg-main-black"
        : ""

    if (swatchImage || swatch.media?.[0]?.url) {
        const checkIfTransformIsNeeded = checkIfImageSrcIsCentraImage(
            swatchImage || swatch.media?.[0]?.url || ""
        )
        const transformedImage = checkIfTransformIsNeeded
            ? transformToBunnyCdn(swatchImage || swatch.media?.[0]?.url || "")
            : swatchImage || swatch.media?.[0]?.url || ""

        return (
            <DynamicLink
                href={`/product/${swatch.uri}`}
                className={clsx(thumbnailStyling, "flex", FOCUS_VISIBLE_INSET_CLASS)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={e => handleActiveThumbnailClick(e)}
                key={`${index}-${swatch?.uri ?? ""}`}
                prefetch={false}
                aria-label={getProductVariantLinkLabel(
                    langCode,
                    swatch.name,
                    swatch.color_swatch?.color_name
                )}
            >
                <div
                    className={clsx(
                        "relative aspect-square h-full w-full",
                        borderStyling
                    )}
                >
                    <Image
                        src={bunnyLoader({
                            src: transformedImage,
                            width: THUMBNAIL_SIZE_PX,
                            height: THUMBNAIL_SIZE_PX,
                            fit: "cover",
                            quality: 70,
                            format: "webp",
                            background: "ffffff",
                            auto: false
                        })}
                        unoptimized
                        width={THUMBNAIL_SIZE_PX}
                        height={THUMBNAIL_SIZE_PX}
                        alt=""
                        loading={isAboveTheFold ? "eager" : "lazy"}
                        className="h-full w-full object-cover object-center"
                    />
                </div>
            </DynamicLink>
        )
    }
}

const INITIAL_VISIBLE_THUMBNAILS = 6
const SENTINEL_AFTER_INDEX = 1

export default function ProductCardThumbnails({
    allVariants,
    langCode,
    showBenefits,
    handleGoToSwatchTracking,
    isAboveTheFold
}: ProductCardThumbnailsProps) {
    const [isScrolled, setIsScrolled] = useState(false)
    const [isScrolledToEnd, setIsScrolledToEnd] = useState(false)
    const [showAll, setShowAll] = useState(
        allVariants.length <= INITIAL_VISIBLE_THUMBNAILS
    )
    const thumbnailsRef = useRef<HTMLDivElement>(null)
    const sentinelRef = useRef<HTMLDivElement>(null)

    const checkScroll = () => {
        if (thumbnailsRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = thumbnailsRef.current
            const isScrolled = scrollLeft > 0
            const isScrolledToEnd = scrollLeft + clientWidth >= scrollWidth
            setIsScrolled(isScrolled)
            setIsScrolledToEnd(isScrolledToEnd)
        }
    }

    useEffect(() => {
        const container = thumbnailsRef.current
        container?.addEventListener("scroll", checkScroll)
        checkScroll()

        return () => {
            container?.removeEventListener("scroll", checkScroll)
        }
    }, [])

    useEffect(() => {
        if (showAll) return
        const el = sentinelRef.current
        const root = thumbnailsRef.current
        if (!el || !root) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShowAll(true)
                    observer.disconnect()
                }
            },
            { root, rootMargin: "0px" }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [showAll])

    const scroll = (direction: "left" | "right") => {
        if (thumbnailsRef.current) {
            let scrollAmount = 220 // width of thumbnailsRef + padding
            if (isScrolledToEnd) scrollAmount = 216 // width of thumbnailsRef
            if (direction === "left") {
                thumbnailsRef.current.scrollBy({
                    left: -scrollAmount,
                    behavior: "smooth"
                })
            } else {
                thumbnailsRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" })
            }
        }
    }

    return (
        <div className="relative flex items-center sm:gap-4">
            {allVariants.length > 5 && isScrolled && (
                <div className="absolute bottom-0 left-0 z-10 h-28 w-[41px] bg-gradient-to-r from-white to-transparent sm:hidden sm:h-32 lg:h-40"></div>
            )}
            {allVariants.length > 5 && isScrolled && (
                <button
                    type="button"
                    aria-label={t(langCode, "product_page.previous_color")}
                    className={clsx(
                        "hidden sm:block",
                        FOCUS_VISIBLE_INSET_CLASS,
                        HIT_TARGET_CLASS
                    )}
                    onClick={() => scroll("left")}
                >
                    <span aria-hidden="true">
                        <IconRoundChevronLeft />
                    </span>
                </button>
            )}
            <div
                ref={thumbnailsRef}
                className={`no-scrollbar relative flex h-28 w-[150px] overflow-hidden overflow-x-scroll px-2 sm:h-32 sm:w-[176px] lg:h-40 lg:w-[216px]`}
            >
                <div
                    className={`flex w-full items-center gap-8 ${showBenefits ? "flex-wrap" : ""}`}
                >
                    <div className="flex gap-6 sm:gap-4">
                        {(showAll
                            ? allVariants
                            : allVariants.slice(0, INITIAL_VISIBLE_THUMBNAILS)
                        ).map((swatch, index) => (
                            <Fragment key={`${index}-${swatch?.uri ?? ""}`}>
                                <Thumbnail
                                    swatch={swatch}
                                    index={index}
                                    isAboveTheFold={isAboveTheFold}
                                    langCode={langCode}
                                    handleGoToSwatchTracking={handleGoToSwatchTracking}
                                />
                                {!showAll && index === SENTINEL_AFTER_INDEX && (
                                    <div
                                        ref={sentinelRef}
                                        aria-hidden
                                        className="h-1 w-px shrink-0"
                                    />
                                )}
                            </Fragment>
                        ))}
                    </div>
                </div>
            </div>
            {allVariants.length > 5 && !isScrolledToEnd && (
                <button
                    type="button"
                    aria-label={t(langCode, "product_page.next_color")}
                    className={clsx(
                        "hidden sm:block",
                        FOCUS_VISIBLE_INSET_CLASS,
                        HIT_TARGET_CLASS
                    )}
                    onClick={() => scroll("right")}
                >
                    <span aria-hidden="true">
                        <IconRoundChevronRight className="flex" />
                    </span>
                </button>
            )}
        </div>
    )
}
